import { Breadcrumb, Form, Input, Pagination, Skeleton, Tabs } from "antd"
import rightArrowIcon from "assets/svg/chevron-right.svg"
import calenderPlusIcon from "assets/svg/CalendarPlus.svg"
import { Button } from "components"
import { useEffect, useState } from "react"
import { useGet } from "utils/useFetch"
import { formatDateReadable } from "utils/formatDate"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useNavigate } from "react-router-dom"
import NamePermissionAndClockIn from "../components/NamePermissionClockIn"
import ShiftPeriodsAndAssignment from "../components/ShiftPeriodsAndAssignment"
import {
  IShiftPlanDoc,
  IDraftShiftDataDocsEntity,
  IShiftPlansRes,
  IDraftShiftDataRes,
  ClockInEmployeeRes,
} from "../typings"
import ConfigurationCard, {
  ConfigurationCardPropsCardStatus,
} from "../components/ConfigurationCard"
import { toast } from "react-toastify"
import CustomEmptyState from "./component/CustomEmptyState"
import { useAuthStore, useAppNotificationStore } from "stores"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import { IUser, UserExtension } from "stores/auth"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import CustomBreadCrumb from "./component/CustomBreadCrumb"

// ─── Constants ────────────────────────────────────────────────────────────────
const INITIAL_PAGE = { currentPage: 1, pageSize: 10 }

const PILL_TABS_CLASS =
  "[&_.ant-tabs-ink-bar]:hidden [&_.ant-tabs-nav]:before:!hidden [&_.ant-tabs-nav]:bg-slate-100 [&_.ant-tabs-tab-btn]:text-black [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-black [&_.ant-tabs-nav]:py-1 [&_.ant-tabs-nav]:px-1.5 [&_.ant-tabs-nav]:rounded-full [&_.ant-tabs-nav]:inline-flex [&_.ant-tabs-nav]:w-fit [&_.ant-tabs-nav]:max-w-full [&_.ant-tabs-tab]:!m-0 [&_.ant-tabs-tab]:px-5 [&_.ant-tabs-tab]:py-2 [&_.ant-tabs-tab]:rounded-full [&_.ant-tabs-tab]:transition [&_.ant-tabs-tab-active]:bg-white [&_.ant-tabs-tab-active]:font-semibold"

const EMPTY_IMAGES: Record<string, string> = {
  active:
    "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/53252fa3-8e1d-47fa-969d-4816aefc1993.png",
  draft:
    "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/b4bbb122-568f-4ddf-94c5-97a4016a7bfb.png",
  archive:
    "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/9647878f-52b3-4c37-9d5b-13ab47601083.png",
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type ShiftPlanBody = {
  isActive?: boolean
  isDeleted?: boolean
  status?: "published" | "draft"
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const shiftPlanKeys = (
  user: (IUser & Partial<UserExtension>) | null,
  dutylocationId: number | "workplace",
  page: typeof INITIAL_PAGE,
  workplaceId: number | undefined
) => ({
  active: `/v1/time-attendance/shift-plans?dutylocationId=${
    dutylocationId === "workplace" ? "null" : dutylocationId
  }&isActive=true&status=published&isDeleted=false&companyId=${
    user?.userCompanyId
  }&page=${page.currentPage}&limit=${
    page.pageSize
  }&workplaceId=${workplaceId}&populate=hardwareDeviceTypes`,
  draft: `/v1/time-attendance/shift-plans?dutylocationId=${
    dutylocationId === "workplace" ? "null" : dutylocationId
  }&isDeleted=false&status=draft&companyId=${user?.userCompanyId}&page=${
    page.currentPage
  }&limit=${
    page.pageSize
  }&workplaceId=${workplaceId}&populate=hardwareDeviceTypes`,
  archive: `/v1/time-attendance/shift-plans?dutylocationId=${
    dutylocationId === "workplace" ? "null" : dutylocationId
  }&isDeleted=true&companyId=${user?.userCompanyId}&page=${
    page.currentPage
  }&limit=${
    page.pageSize
  }&workplaceId=${workplaceId}&populate=hardwareDeviceTypes`,
})

// ─── Sub-components ───────────────────────────────────────────────────────────

const EmptyState = ({ tab }: { tab: keyof typeof EMPTY_IMAGES }) => (
  <img
    src={EMPTY_IMAGES[tab]}
    className="object-contain"
    alt={`No ${tab} shift plans`}
  />
)

const LoadingSkeleton = () => (
  <div className="border-2 border-[#E2E8F0] rounded-md p-2 mt-2 w-full">
    <div className="flex justify-between items-center gap-5 w-full mb-3">
      <Skeleton.Input
        active
        size="small"
        style={{ width: 20 }}
      />
      <Skeleton.Input
        active
        size="small"
        style={{ width: 20 }}
      />
    </div>
    <Skeleton
      active
      paragraph={{ rows: 1, width: "100%" }}
      style={{ width: "100%" }}
    />
    <div className="flex justify-center items-center">
      <Skeleton.Input
        className="mt-3"
        active
        style={{ width: "100%" }}
      />
    </div>
  </div>
)

// ─── Component ────────────────────────────────────────────────────────────────

const ConfigurationWorkplace = ({
  workplaceId,
  dutylocationId,
}: {
  dutylocationId: number | "workplace"
  workplaceId: string | undefined
}) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast: appToast } = useAppNotificationStore()
  const [canManageShiftPlans] = usePermissions({
    allowedPermissions: [TimeAndAttendancePermission.CanManageShiftPlans],
  })

  const [isEditing, setIsEditing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [modalDetails, setModalDetails] = useState<{
    id: number
    name: string
    numberOfEmployees: number
  } | null>(null)
  const [selectedShiftPlanId, setSelectedShiftPlanId] = useState<number>()
  const [foundShiftPlan, setFoundShiftPlan] = useState<
    IShiftPlanDoc | IDraftShiftDataDocsEntity | undefined
  >()
  const { user } = useAuthStore((state) => state)
  const [page, setPage] = useState(INITIAL_PAGE)

  const keys = shiftPlanKeys(user!, dutylocationId, page, Number(workplaceId))
  const [form] = Form.useForm()

  useEffect(() => {
    setIsEditing(false)
    setSelectedShiftPlanId(undefined)
    setFoundShiftPlan(undefined)
  }, [dutylocationId, workplaceId])

  // ─── Query invalidation ───────────────────────────────────────────────────

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: [keys.active] })
    queryClient.invalidateQueries({ queryKey: [keys.draft] })
    queryClient.invalidateQueries({ queryKey: [keys.archive] })
  }

  // ─── Data fetching ────────────────────────────────────────────────────────

  const { data: archiveShiftData, isLoading: isLoadingArchive } =
    useGet<IShiftPlansRes>({
      url: keys.archive,
      instanceKey: "ewaAxiosInstance",
      enabled: !!dutylocationId,
    })

  const { data: activeShiftData, isLoading: isLoadingActive } =
    useGet<IShiftPlansRes>({
      url: keys.active,
      instanceKey: "ewaAxiosInstance",
      enabled: !!dutylocationId,
    })

  const { data: draftShiftData, isLoading: isLoadingDraft } =
    useGet<IDraftShiftDataRes>({
      url: keys.draft,
      instanceKey: "ewaAxiosInstance",
      enabled: !!dutylocationId,
    })

  const { data: clockedInEmployee, isLoading: isLoadingClockedInEmployee } =
    useGet<ClockInEmployeeRes>({
      url: `/v1/time-attendance/shift-plans/${modalDetails?.id}/open-clockIns/count`,
      instanceKey: "ewaAxiosInstance",
      enabled: !!modalDetails?.id,
    })

  // ─── Mutations ────────────────────────────────────────────────────────────

  const {
    mutate: archiveShiftPlan,
    isLoading: isArchiving,
    variables: archivingVariables,
  } = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ShiftPlanBody }) =>
      ewaAxiosInstance.post(
        `/v1/time-attendance/shift-plans/${id}/archive`,
        body
      ),
    onSuccess: () => {
      toast.success("Shift plan archived successfully")
      invalidateQueries()
    },
    onError: onQueryError,
  })
  const {
    mutate: deleteShiftPlan,
    isLoading: isDeletingShiftPlan,
    variables: deleteVariables,
  } = useMutation({
    mutationFn: ({ id }: { id: number }) =>
      ewaAxiosInstance.delete(`/v1/time-attendance/shift-plans/${id}`),
    onSuccess: () => {
      toast.success("Shift plan deleted successfully")
      invalidateQueries()
      setIsDeleting(false)
    },
    onError: onQueryError,
  })

  const {
    mutate: restoreShiftPlan,
    isLoading: isRestoring,
    variables: restoringVariables,
  } = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ShiftPlanBody }) =>
      ewaAxiosInstance.post(
        `/v1/time-attendance/shift-plans/${id}/restore`,
        body
      ),
    onSuccess: () => {
      toast.success("Shift plan restored successfully")
      invalidateQueries()
    },
    onError: onQueryError,
  })

  const { mutate: updateShiftPlan } = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, any> }) =>
      ewaAxiosInstance.put(`/v1/time-attendance/shift-plans/${id}`, body),
    onSuccess: () => {
      toast.success("Changes Saved")
      invalidateQueries()
    },
    onError: onQueryError,
  })

  const { mutate: toggleShiftPlan, isLoading: isToggling } = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ShiftPlanBody }) =>
      ewaAxiosInstance.put(`/v1/time-attendance/shift-plans/${id}`, body),
    onSuccess: () => {
      toast.success("Shift plan updated successfully")
      invalidateQueries()
    },
    onError: onQueryError,
  })

  // ─── Handlers ────────────────────────────────────────────────────────────

  const openEdit = (
    id: number,
    plan: IShiftPlanDoc | IDraftShiftDataDocsEntity
  ) => {
    if (!canManageShiftPlans) {
      appToast.unauthorized()
      return
    }
    setSelectedShiftPlanId(id)
    setFoundShiftPlan(plan)
    setIsEditing(true)
  }

  const handleNavigateCreate = () => {
    if (!canManageShiftPlans) {
      appToast.unauthorized()
      return
    }
    navigate(
      `/dashboard/time-and-attendance/create-shift-plan-workplace/${workplaceId}`
    )
  }

  // ─── Edit view ────────────────────────────────────────────────────────────

  if (isEditing) {
    return (
      <div className="flex flex-col gap-3">
        <CustomBreadCrumb
          onClick={() => setIsEditing(false)}
          prefixName="Configuration"
          suffixName={foundShiftPlan?.title || ""}
        />
        <Tabs
          defaultActiveKey="1"
          className={PILL_TABS_CLASS}
          destroyInactiveTabPane
          key={selectedShiftPlanId}
          items={[
            {
              key: "1",
              label: "Name, Permission & Clock-in",
              children: foundShiftPlan ? (
                <NamePermissionAndClockIn
                  key={foundShiftPlan.id}
                  shiftPlan={foundShiftPlan}
                  onUpdate={(id, body) => {
                    if (typeof body?.title === "string") {
                      setFoundShiftPlan((prev) =>
                        prev ? { ...prev, title: body.title } : prev
                      )
                    }
                    updateShiftPlan({ id, body })
                  }}
                />
              ) : null,
            },
            {
              key: "2",
              label: "Shift periods and assignment",
              children: (
                <ShiftPeriodsAndAssignment
                  key={`${selectedShiftPlanId ?? ""}-${dutylocationId}`}
                  shiftPlanId={selectedShiftPlanId}
                  dutyLocationId={dutylocationId}
                  shiftPlan={foundShiftPlan}
                />
              ),
            },
          ]}
        />
      </div>
    )
  }

  // ─── Main view ────────────────────────────────────────────────────────────

  return (
    <>
      <Tabs
        defaultActiveKey="1"
        className={PILL_TABS_CLASS}
        items={[
          {
            key: "1",
            label: "Active shift plans",
            children:
              isLoadingActive || isToggling ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <LoadingSkeleton key={i} />
                  ))}
                </div>
              ) : (
                <div>
                  {activeShiftData?.data?.docs?.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2">
                      {activeShiftData?.data.docs.map((x) => (
                        <ConfigurationCard
                          flaggedForDeletion={x.flaggedForDeletion}
                          key={x.id}
                          date={`${formatDateReadable(
                            x.startDate
                          )} to ${formatDateReadable(x.endDate)}`}
                          title={x.title}
                          status={x.status as ConfigurationCardPropsCardStatus}
                          employeeCount={x?.totalEmployees}
                          shiftCount={x?.totalShiftPeriods}
                          onEdit={() => openEdit(x.id, x)}
                          onArchive={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            archiveShiftPlan({
                              id: x.id,
                              body: { isDeleted: true },
                            })
                          }}
                          onDelete={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            setModalDetails({
                              id: x.id,
                              name: x.title,
                              numberOfEmployees: x.totalEmployees,
                            })
                            setIsDeleting(true)
                          }}
                          isDeletingShiftPlan={
                            isDeletingShiftPlan && deleteVariables?.id === x.id
                          }
                          isLoading={
                            isArchiving && archivingVariables?.id === x.id
                          }
                          onToggle={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            toggleShiftPlan({
                              id: x.id,
                              body: { isActive: false, status: "draft" },
                            })
                          }}
                        />
                      ))}
                      <CreateShiftPlanButton
                        handleNavigateCreate={handleNavigateCreate}
                      />
                    </div>
                  ) : (
                    <CustomEmptyState
                      imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/334e4ce4-2ca3-422c-be4b-eb93727cae71.png"
                      onClick={handleNavigateCreate}
                      title="Create shift plan"
                    />
                  )}
                  {/* Pagination */}
                  <div className="flex justify-start mt-6 mb-3">
                    <Pagination
                      current={page.currentPage}
                      pageSize={page.pageSize}
                      total={activeShiftData?.data?.totalDocs || 0}
                      showSizeChanger
                      pageSizeOptions={[5, 10, 20, 50]}
                      onChange={(page, pageSize) =>
                        setPage({ currentPage: page, pageSize })
                      }
                    />
                  </div>
                </div>
              ),
          },
          {
            key: "2",
            label: "Draft plans",
            children:
              isLoadingDraft || isToggling ? (
                <LoadingSkeleton />
              ) : (
                <div>
                  {draftShiftData?.data?.docs?.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2">
                      {draftShiftData.data.docs.map((x) => (
                        <ConfigurationCard
                          key={x.id}
                          date={`${formatDateReadable(
                            x.startDate
                          )} to ${formatDateReadable(x.endDate)}`}
                          title={x.title}
                          status={x.status as ConfigurationCardPropsCardStatus}
                          employeeCount={x?.totalEmployees || 0}
                          shiftCount={x?.totalShiftPeriods || 0}
                          onEdit={() => openEdit(x.id, x)}
                          onRestore={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            restoreShiftPlan({
                              id: x.id,
                              body: { isDeleted: false },
                            })
                          }}
                          onDelete={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            setModalDetails({
                              id: x.id,
                              name: x.title,
                              numberOfEmployees: x.totalEmployees,
                            })
                            setIsDeleting(true)
                          }}
                          isRestoreLoading={isRestoring}
                          onToggle={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            toggleShiftPlan({
                              id: x.id,
                              body: { isActive: true, status: "published" },
                            })
                          }}
                          onArchive={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            archiveShiftPlan({
                              id: x.id,
                              body: { isDeleted: true },
                            })
                          }}
                          isLoading={
                            isArchiving && archivingVariables?.id === x.id
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <CustomEmptyState
                      imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/924487cc-4c5e-483e-926e-49862c95ea5a.png"
                      onClick={handleNavigateCreate}
                      title="Create shift plan"
                    />
                  )}
                  {/* Pagination */}
                  <div className="flex justify-start mt-6 mb-3">
                    <Pagination
                      current={page.currentPage}
                      pageSize={page.pageSize}
                      total={activeShiftData?.data?.totalDocs || 0}
                      showSizeChanger
                      pageSizeOptions={[5, 10, 20, 50]}
                      onChange={(page, pageSize) =>
                        setPage({ currentPage: page, pageSize })
                      }
                    />
                  </div>
                </div>
              ),
          },
          {
            key: "3",
            label: "Archived / Expired plans",
            children:
              isLoadingArchive || isToggling ? (
                <LoadingSkeleton />
              ) : (
                <div>
                  {archiveShiftData?.data?.docs?.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2">
                      {archiveShiftData.data.docs.map((x) => (
                        <ConfigurationCard
                          key={x.id}
                          date={`${formatDateReadable(
                            x.startDate
                          )} to ${formatDateReadable(x.endDate)}`}
                          title={x.title}
                          status={x.isDeleted}
                          employeeCount={x?.shiftAssignments?.length || 0}
                          shiftCount={x?.shiftPeriods?.length || 0}
                          onEdit={() => openEdit(x.id, x)}
                          onDelete={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            deleteShiftPlan({ id: x.id })
                          }}
                          onRestore={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            restoreShiftPlan({
                              id: x.id,
                              body: { isDeleted: false },
                            })
                          }}
                          isRestoreLoading={
                            isRestoring && restoringVariables?.id === x.id
                          }
                          onToggle={() => {
                            if (!canManageShiftPlans) {
                              appToast.unauthorized()
                              return
                            }
                            toggleShiftPlan({
                              id: x.id,
                              body: { isDeleted: false },
                            })
                          }}
                          isLoading={
                            isArchiving && archivingVariables?.id === x.id
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <CustomEmptyState
                      imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/d4dc77a4-5bca-408a-b189-784c11d7ea13.png"
                      onClick={handleNavigateCreate}
                      title="Create shift plan"
                    />
                  )}
                  {/* Pagination */}
                  <div className="flex justify-start mt-6 mb-3">
                    <Pagination
                      current={page.currentPage}
                      pageSize={page.pageSize}
                      total={activeShiftData?.data?.totalDocs || 0}
                      showSizeChanger
                      pageSizeOptions={[5, 10, 20, 50]}
                      onChange={(page, pageSize) =>
                        setPage({ currentPage: page, pageSize })
                      }
                    />
                  </div>
                </div>
              ),
          },
        ]}
      />

      <CustomModal
        rootClassName="ta-scope"
        open={isDeleting}
        title="Delete shift plan"
        onCancel={() => {
          setIsDeleting(false)
          form.resetFields()
        }}
        footer={
          <div className="w-full justify-end flex flex-col gap-2">
            <Button
              color="danger"
              submitting={isDeletingShiftPlan}
              onClick={() => form.submit()}
            >
              {clockedInEmployee?.data?.employeesClockedIn! <= 0
                ? "Delete now"
                : "Schedule for deletion"}
            </Button>
            <Button
              className="font-semibold border-2 border-slate-300 rounded-md"
              onClick={() => setIsDeleting(false)}
              disabled={isDeletingShiftPlan}
            >
              Go back
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          <p className="font-semibold mb-1 flex items-center gap-1 text-[16px]">
            Delete this shift plan?
          </p>
          <p className="text-sm  text-slate-500">
            This will permanently remove the shift plan and all its shift
            periods. Employees currently assigned to this plan will be
            unassigned. This action cannot be undone.
          </p>
          <div className="bg-[#FEF2F2] flex gap-3 items-center p-3 rounded-md">
            <img
              src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/4eaed76f-0885-438b-a354-12797ff5cb86.png"
              width={32}
              height={32}
            />
            <p className="text-[#E7000B] text-sm">
              {clockedInEmployee?.data?.employeesClockedIn || 0} employees are
              currently clocked into a shift in this plan. The plan will be
              removed when all employee(s) clock-out.
            </p>
          </div>
          <p className="text-sm  text-slate-500">
            If you continue, this shift plan will be scheduled for deletion:
          </p>
          <p className="text-sm text-slate-500">
            - No new shifts can be started or scheduled
            <br /> - No new employees can be assigned
            <br /> - Active shifts will continue until employees clock out
            normally
            <br /> - Once the last active shift ends, the plan and all its shift
            periods will be permanently removed
          </p>
          <p>
            To confirm, enter the shift plan name below: {modalDetails?.name}
          </p>
          <Form
            form={form}
            onFinish={() => deleteShiftPlan({ id: modalDetails?.id! })}
          >
            <Form.Item
              name="confirmationKeyword"
              validateTrigger="onSubmit"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value)
                      return Promise.reject(
                        new Error("Please enter the shift plan name")
                      )
                    if (value !== modalDetails?.name)
                      return Promise.reject(
                        new Error(
                          `Name does not match — enter exactly: ${modalDetails?.name}`
                        )
                      )
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Input
                placeholder="Enter confirmation keyword"
                className="h-10"
              />
            </Form.Item>
          </Form>
        </div>
      </CustomModal>
    </>
  )
}

export default ConfigurationWorkplace

export const CreateShiftPlanButton = ({
  handleNavigateCreate,
}: {
  handleNavigateCreate: () => void
}) => {
  return (
    <Button
      color="neutral"
      className="flex items-center py-2 justify-center flex-col min-h-[10rem] h-full w-full bg-white border border-slate-200"
      onClick={handleNavigateCreate}
    >
      <img
        src={calenderPlusIcon}
        height={30}
        width={30}
        alt="create shift plan"
      />
      <span className="flex items-center gap-2 font-semibold">
        Create shift plan
        <img
          src={rightArrowIcon}
          width={15}
          height={15}
          alt="arrow"
        />
      </span>
    </Button>
  )
}
